import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiCheckCircle,
  FiArrowRight,
  FiArrowLeft,
  FiCheckSquare,
  FiKey,
  FiTrendingUp,
  FiCalendar,
} from 'react-icons/fi';
import Sidebar from '../core/Sidebar';
import EnglishSidebar from '../core/exams/english/EnglishSidebar';
import GetStartedWith from './GetStartedWith';
import { ENGLISH_SIDEBAR_CATEGORIES } from '../utils/englishCategories';
import { sanitizeHtml } from '../utils/safeHtml';
import './IELTSCourseTips.css';

/**
 * IELTSCourseTips - Reusable component for IELTS tips pages
 *
 * @param {Object} props
 * @param {string} props.category - Category name (reading, writing, listening, speaking)
 * @param {React.ReactNode} props.icon - Icon component for the header
 * @param {string} props.title - Page title
 * @param {Array} props.overview - Array of overview items { icon, label, value }
 * @param {Array} props.tips - Array of tip sections { title, content, bullets?, highlight? }
 * @param {Array} props.extraStrategies - Array of extra strategy items
 * @param {Array} props.improvementTips - Array of improvement tips { icon, label, text }
 * @param {Object} props.aiHelp - AI help configuration { title, context, firstMessage }
 */
export default function IELTSCourseTips({
  category,
  icon,
  title,
  overview = [],
  tips = [],
  extraStrategies = [],
  improvementTips = [],
  aiHelp = {},
}) {
  const navigate = useNavigate();
  const categoryCapitalized =
    category.charAt(0).toUpperCase() + category.slice(1);

  const handleCategorySelect = (newCategory) => {
    if (newCategory === 'progression') {
      navigate('/exams/english/progression');
    } else if (newCategory === 'journey') {
      navigate('/exams/english/journey');
    } else if (newCategory === 'speaking') {
      navigate('/exams/english/speaking');
    } else {
      navigate('/exams/english/', {
        state: { activeCategory: newCategory },
      });
    }
  };

  return (
    <div className="ielts-tips-layout">
      <Sidebar />
      <div className="ielts-tips-content-wrapper">
        <main className="ielts-tips-main">
          <button
            className="ielts-tips-back-btn"
            onClick={() =>
              navigate(`/exams/english/`, {
                state: { activeCategory: category },
              })
            }
          >
            <FiArrowLeft /> Back to {categoryCapitalized} Section
          </button>

          <div className="ielts-tips-container">
            {/* Header */}
            <h1 className="ielts-tips-header">
              <span className="ielts-tips-header-icon">{icon}</span>
              {title}
            </h1>

            {/* Overview Card */}
            {overview.length > 0 && (
              <div className="ielts-tips-overview">
                {overview.map((item, index) => (
                  <div key={index} className="ielts-tips-overview-item">
                    {item.icon}
                    <strong>{item.label}</strong>
                    <span>{item.value}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Tips Section Title */}
            <h2 className="ielts-tips-section-title">
              <FiCheckSquare /> Top Tips to Improve Your IELTS{' '}
              {categoryCapitalized} Score
            </h2>

            {/* Tips Cards */}
            {tips.map((tip, index) => (
              <div key={index} className="ielts-tip-card">
                <h3 className="ielts-tip-card-title">
                  <span className="ielts-tip-number">{index + 1}</span>
                  {tip.title}
                </h3>

                {tip.content && (
                  <div className="ielts-tip-content">
                    {Array.isArray(tip.content) ? (
                      tip.content.map((para, i) => (
                        <p
                          key={i}
                          dangerouslySetInnerHTML={{ __html: sanitizeHtml(para) }}
                        />
                      ))
                    ) : (
                      <p
                        dangerouslySetInnerHTML={{
                          __html: sanitizeHtml(tip.content),
                        }}
                      />
                    )}
                  </div>
                )}

                {tip.bullets && (
                  <ul className="ielts-tip-bullets">
                    {tip.bullets.map((bullet, i) => (
                      <li key={i}>
                        <FiCheckCircle />
                        {bullet}
                      </li>
                    ))}
                  </ul>
                )}

                {tip.highlight && (
                  <div className="ielts-tip-highlight">
                    <FiArrowRight />
                    <span
                      dangerouslySetInnerHTML={{
                        __html: sanitizeHtml(tip.highlight),
                      }}
                    />
                    {tip.linkText && tip.linkPath && (
                      <button
                        className="btn-lwz-primary ielts-tip-link-btn"
                        onClick={() =>
                          navigate(tip.linkPath, {
                            state: {
                              activeCategory: tip.linkCategory || category,
                            },
                          })
                        }
                      >
                        {tip.linkText} <FiArrowRight />
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))}

            {/* Extra Strategies */}
            {extraStrategies.length > 0 && (
              <div className="ielts-tips-extra">
                <h3>
                  <FiKey /> Extra Strategies
                </h3>
                <ul>
                  {extraStrategies.map((strategy, index) => (
                    <li key={index}>
                      <FiCheckCircle />
                      {strategy}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Improvement Section */}
            {improvementTips.length > 0 && (
              <div className="ielts-tips-improvement">
                <h3>
                  <FiTrendingUp /> How to Improve Over Time
                </h3>
                <ul>
                  {improvementTips.map((tip, index) => (
                    <li key={index}>
                      {tip.icon || <FiCalendar />}
                      <strong>{tip.label}</strong>
                      <span>{tip.text}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* AI Help Section */}
            {aiHelp.title && (
              <div className="ielts-tips-help">
                <GetStartedWith
                  title={aiHelp.title}
                  spaceName={`IELTS ${categoryCapitalized}`}
                  humanCoachTitle="Talk to an IELTS coach"
                  liwazaAITitle="Ask AI"
                  communityTitle="Ask other students"
                  showHumanCoach={true}
                  showLiwazaAI={true}
                  showCommunity={true}
                  aifirstmessage={aiHelp.firstMessage}
                  aicontext={aiHelp.context}
                />
              </div>
            )}
          </div>
        </main>
        <EnglishSidebar
          categories={ENGLISH_SIDEBAR_CATEGORIES}
          selectedCategory={category}
          setSelectedCategory={handleCategorySelect}
        />
      </div>
    </div>
  );
}
